/* Realtime voice client.
 *
 * Two transports against the OpenQwenChat gateway, sharing one event model:
 *   - 'webrtc' (default): low-latency.  Mic audio goes as RTP tracks; the model's
 *     audio arrives on a remote track; control events ride a DataChannel.
 *   - 'ws':  mic captured as PCM16@16k via an AudioWorklet and sent as
 *     input_audio_buffer.append; model audio arrives as response.audio.delta
 *     (PCM16@24k) and is played through a second AudioWorklet.
 *
 * Handlers: { onStatus, onSpeechStart, onUserText, onAssistantText, onError }
 *   status in: connecting | listening | thinking | speaking | denied | closed
 *   onSpeechStart(itemId)            — a new user speech segment began
 *   onUserText(acc, final, itemId)   — running/final transcript for that segment
 *   onAssistantText(acc, final, respId) — running/final reply transcript
 * ids are stable per segment/reply so the UI can keep many bubbles in order.
 */

import { isArchiveError } from "./courseArchive.js";
import { createCourseHistoryReceiver } from './courseHistory.js';

const recorderUrl = new URL("../worklets/pcm-recorder.js", import.meta.url);
const playerUrl = new URL("../worklets/pcm-player.js", import.meta.url);

function b64ToInt16(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Int16Array(bytes.buffer);
}
function int16ToB64(i16) {
  const bytes = new Uint8Array(i16.buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function wsBase() {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}`;
}

export class RealtimeClient {
  constructor({ transport = "webrtc", voice = "Chelsie", model = "qwen3-omni", instructions = "", realtimeMode = "", thinking = undefined, courseId = "", handlers = {}, webSocketFactory = url => new WebSocket(url) }) {
    this.webSocketFactory = webSocketFactory;
    // Upstream conversation mode for the local Omni: "chat" = interleaved
    // /v1/chat/completions, "realtime" = native /v1/realtime WS bridge (separate
    // upstream). Sent to the gateway as a per-session hint; "" = gateway default.
    this.realtimeMode = (realtimeMode || "").toLowerCase();
    this.transport = transport === "auto" ? "webrtc" : transport;
    this.voice = voice;
    this.model = model;
    this.instructions = instructions;
    // Thinking switch (top bar). The gateway turns it into the Qwen3 template's
    // enable_thinking; undefined leaves the model's registry default alone.
    this.thinking = typeof thinking === "boolean" ? thinking : undefined;
    // Course mode (harness service): a course_id in session.update makes the
    // gateway load the training-rendered course state and speak the opener
    // turn first. agent.update events feed the UI's agent panel.
    this.courseId = courseId || "";
    this.h = handlers;
    this.historyReceiver = createCourseHistoryReceiver(handlers.onHistoryMessage);

    this.stream = null;
    this.muted = false;
    this.archivePending = false;
    this.archiving = false;
    this.courseFinished = false;
    this.closed = false;
    this.status = "connecting";
    this.generating = false;
    this.textReady = false;
    // Per-id accumulators so overlapping / out-of-order streams (independent
    // per-segment ASR + the reply) each land in their own bubble. Keyed by the
    // server's stable ids: item_id for user segments, response_id for replies.
    this.userAcc = {};        // item_id -> accumulated transcript
    this.asstAcc = {};        // response_id -> accumulated transcript
    this.reasonAcc = {};      // response_id -> accumulated reasoning (separate)
    this.curRespId = null;    // most recent response_id (for legacy events)

    // transport handles
    this.ws = null;
    this.pc = null;
    this.dc = null;
    this.remoteAudio = null;
    this.captureCtx = null;
    this.playCtx = null;
    this.playerNode = null;
    this.levelCtx = null;
    this.analyser = null;
    this._levelData = null;
  }

  // ----------------------------------------------------------------- lifecycle
  async start() {
    if (this.closed) return;
    this._status("connecting");
    try {
      // Full-duplex: enable echo cancellation so the model's own speech played
      // through the speakers doesn't feed back into the mic and self-interrupt.
      const audioConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    } catch (e) {
      if (this.closed) return;
      this._status("denied");
      this._err("Microphone unavailable: " + e.message);
      return;
    }
    // Model switches can unmount the call while device permission is pending.
    if (this.closed) { this.stop(); return; }
    this._setupLevelAnalyser();
    try {
      if (this.transport === "webrtc") await this._startWebrtc();
      else await this._startWs();
    } catch (e) {
      this._err("Realtime connection failed: " + e.message);
      this._status("closed");
    }
  }

  stop() {
    this.closed = true;
    try { this.ws && this.ws.close(); } catch {}
    try { this.dc && this.dc.close(); } catch {}
    try { this.pc && this.pc.close(); } catch {}
    try { this.playerNode && this.playerNode.port.postMessage({ type: "clear" }); } catch {}
    for (const ctx of [this.captureCtx, this.playCtx, this.levelCtx]) {
      try { ctx && ctx.close(); } catch {}
    }
    if (this.remoteAudio) { try { this.remoteAudio.srcObject = null; this.remoteAudio.remove(); } catch {} }
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
  }

  setMuted(m) {
    this.muted = m;
    if (this.stream) this.stream.getAudioTracks().forEach((t) => (t.enabled = !m && !this.archivePending));
  }

  getInputLevel() {
    if (!this.analyser) return 0;
    this.analyser.getByteTimeDomainData(this._levelData);
    let sum = 0;
    for (let i = 0; i < this._levelData.length; i++) {
      const v = (this._levelData[i] - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / this._levelData.length) * 3.4);
  }

  // ------------------------------------------------------------------ helpers
  _status(s) { this.status = s; this.h.onStatus && this.h.onStatus(s); }
  _err(m, error) { this.h.onError && this.h.onError(m, error); }

  /** Change the Thinking switch mid-call. The gateway reads session.thinking per
   *  turn, so a session.update is enough — reconnecting would drop the in-flight
   *  reply and show "Disconnected". */
  setThinking(thinking) {
    const next = typeof thinking === "boolean" ? thinking : undefined;
    if (next === this.thinking) return;
    this.thinking = next;
    const evt = JSON.stringify({ type: "session.update", session: this._sessionConfig() });
    try {
      if (this.ws && this.ws.readyState === 1) this.ws.send(evt);
      else if (this.dc && this.dc.readyState === "open") this.dc.send(evt);
    } catch {}
  }

  _sessionConfig() {
    return {
      model: this.model || undefined,
      modalities: ["text", "audio"],
      voice: this.voice || undefined,
      instructions: this.instructions || undefined,
      input_audio_format: "pcm16",
      output_audio_format: "pcm16",
      turn_detection: { type: "server_vad" },
      ...(this.thinking === undefined ? {} : { thinking: this.thinking }),
      ...(this.courseId ? { course_id: this.courseId } : {}),
    };
  }

  _sendCtl(obj) {
    if (this.closed) return false;
    const evt = JSON.stringify(obj);
    try {
      if (this.ws && this.ws.readyState === 1) this.ws.send(evt);
      else if (this.dc && this.dc.readyState === "open") this.dc.send(evt);
      else return false;
      return true;
    } catch {}
    return false;
  }

  get canSendText() {
    return this.textReady && !this.closed && !this.archivePending && !this.courseFinished
      && !this.generating && this.status === "listening";
  }

  sendText(text) {
    const value = (text || "").trim();
    if (!value || !this.canSendText) return false;
    if (!this._sendCtl({ type: "input_text", text: value })) return false;
    this.playerNode?.port.postMessage({ type: "clear" });
    this.generating = true;
    this._status("thinking");
    return true;
  }

  /** Attach (or switch) the harness course mid-call: the gateway reloads the
   *  course state and the counselor re-opens with「这是第k次会话」. */
  setCourse(courseId) {
    if (this.archivePending && !this.courseFinished) return false;
    const next = courseId || "";
    if (next === this.courseId) return;
    this.courseId = next;
    this._sendCtl({ type: "session.update", session: this._sessionConfig() });
  }

  /** End-of-session archival: the gateway posts the transcript (WITH think) to
   *  the harness service; summary/profile come back as agent.update events. */
  finishCourse() {
    if (this.archiving || this.courseFinished || !this.courseId) return false;
    if (!this._sendCtl({ type: "course.finish" })) return false;
    this.archivePending = true;
    this.archiving = true;
    this.setMuted(this.muted);
    return true;
  }

  /** Abandon a running course before archival starts. An attempted archive
   *  must resolve its receipt by retry; disconnect does not prove non-commit. */
  interruptCourse() {
    if (this.archivePending) return false;
    return this._sendCtl({ type: "course.interrupt" });
  }

  _setupLevelAnalyser() {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      this.levelCtx = new AC();
      const src = this.levelCtx.createMediaStreamSource(this.stream);
      const an = this.levelCtx.createAnalyser();
      an.fftSize = 512;
      an.smoothingTimeConstant = 0.8;
      src.connect(an);
      this.analyser = an;
      this._levelData = new Uint8Array(an.fftSize);
    } catch { /* viz is best-effort */ }
  }

  // Shared event handling for both transports (events use the same wire names).
  _handleEvent(evt) {
    if (this.closed) return;
    switch (evt.type) {
      case "session.created":
        break;
      case "session.updated":
        this.textReady = true;
        if (!this.generating) this._status("listening");
        break;
      case "input_audio_buffer.speech_started":
        this.generating = true;
        // barge-in: stop any current playback
        if (this.playerNode) this.playerNode.port.postMessage({ type: "clear" });
        this.h.onSpeechStart && this.h.onSpeechStart(evt.item_id);
        this._status("listening");
        break;
      case "input_audio_buffer.committed":
        this.generating = true;
        this._status("thinking");
        break;
      case "conversation.item.input_audio_transcription.delta": {
        const id = evt.item_id || "u?";
        this.userAcc[id] = (this.userAcc[id] || "") + (evt.delta || "");
        this.h.onUserText && this.h.onUserText(this.userAcc[id], false, id);
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const id = evt.item_id || "u?";
        const full = evt.transcript || this.userAcc[id] || "";
        this.userAcc[id] = full;
        this.h.onUserText && this.h.onUserText(full, true, id);
        break;
      }
      case "response.created":
        this.generating = true;
        this.curRespId = evt.response_id || null;
        if (this.curRespId) {
          this.asstAcc[this.curRespId] = "";
          this.reasonAcc[this.curRespId] = "";
        }
        this._status("thinking");
        break;
      case "response.reasoning.started": {
        const id = evt.response_id || this.curRespId || "r?";
        this.reasonAcc[id] = "";
        this._status("thinking");
        this.h.onReasoningStart && this.h.onReasoningStart(id);
        break;
      }
      case "response.reasoning.delta": {
        const id = evt.response_id || this.curRespId || "r?";
        this.reasonAcc[id] = (this.reasonAcc[id] || "") + (evt.delta || "");
        this.h.onReasoningText && this.h.onReasoningText(
          this.reasonAcc[id], false, id, null
        );
        break;
      }
      case "response.reasoning.done": {
        const id = evt.response_id || this.curRespId || "r?";
        const full = evt.reasoning ?? this.reasonAcc[id] ?? "";
        const duration = Number(evt.duration_ms);
        this.reasonAcc[id] = full;
        this.h.onReasoningText && this.h.onReasoningText(
          full, true, id, Number.isFinite(duration) ? duration : 0
        );
        break;
      }
      case "response.audio_transcript.delta": {
        const id = evt.response_id || this.curRespId || "r?";
        this.asstAcc[id] = (this.asstAcc[id] || "") + (evt.delta || "");
        this._status("speaking");
        this.h.onAssistantText && this.h.onAssistantText(this.asstAcc[id], false, id);
        break;
      }
      case "response.audio_transcript.done": {
        const id = evt.response_id || this.curRespId || "r?";
        const full = evt.transcript || this.asstAcc[id] || "";
        this.asstAcc[id] = full;
        this.h.onAssistantText && this.h.onAssistantText(full, true, id);
        break;
      }
      case "response.audio.delta":
        this._status("speaking");
        if (evt.delta) this._playPcm(b64ToInt16(evt.delta));
        break;
      case "response.audio.done":
        break;
      case "response.done":
        this.generating = false;
        this._status("listening");
        break;
      // course mode's slow lane: harness state at session start, archival
      // progress and the extracted summary/profile (the UI's agent panel).
      case "agent.update":
        if (evt.kind === 'session.history') this.historyReceiver.push(evt.data);
        if (evt.kind === "session.finishing") {
          this.archivePending = true;
          this.archiving = true;
          this.setMuted(this.muted);
        }
        if (evt.kind === "session.finish") {
          this.courseFinished = true;
          this.archiving = false;
          this.archivePending = true;
          this.setMuted(this.muted);
        }
        if (evt.kind === "session.start" || evt.kind === "session.interrupt") {
          this.archivePending = false;
          this.archiving = false;
          this.courseFinished = false;
          if (evt.kind === "session.interrupt") this.courseId = "";
          this.setMuted(this.muted);
        }
        this.h.onAgentUpdate && this.h.onAgentUpdate(evt.kind, evt.data || {});
        break;
      case "error":
        if (isArchiveError(evt.error) || this.archivePending) {
          this.archivePending = evt.error?.type !== "empty_session";
          this.archiving = false;
          this.setMuted(this.muted);
        }
        this._err((evt.error && evt.error.message) || "server error", evt.error);
        break;
    }
  }

  _playPcm(i16) {
    if (!this.playerNode) return;
    const f32 = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
    this.playerNode.port.postMessage(f32, [f32.buffer]);
  }

  // ----------------------------------------------------------------- WS path
  async _startWs() {
    const AC = window.AudioContext || window.webkitAudioContext;

    // playback @24k
    this.playCtx = new AC({ sampleRate: 24000 });
    await this.playCtx.audioWorklet.addModule(playerUrl);
    if (this.closed) return;
    this.playerNode = new AudioWorkletNode(this.playCtx, "pcm-player");
    this.playerNode.connect(this.playCtx.destination);

    // capture @16k
    this.captureCtx = new AC({ sampleRate: 16000 });
    await this.captureCtx.audioWorklet.addModule(recorderUrl);
    if (this.closed) return;
    const src = this.captureCtx.createMediaStreamSource(this.stream);
    const rec = new AudioWorkletNode(this.captureCtx, "pcm-recorder");
    const zero = this.captureCtx.createGain();
    zero.gain.value = 0;
    src.connect(rec);
    rec.connect(zero);
    zero.connect(this.captureCtx.destination);
    rec.port.onmessage = (e) => {
      if (this.muted || this.archivePending || !this.ws || this.ws.readyState !== 1) return;
      const b64 = int16ToB64(new Int16Array(e.data));
      this.ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: b64 }));
    };

    // Forward the chosen upstream conversation mode.
    let wsUrl = `${wsBase()}/v1/realtime`;
    const qs = new URLSearchParams();
    if (this.realtimeMode) qs.set("realtime_mode", this.realtimeMode);
    const qstr = qs.toString();
    if (qstr) wsUrl += "?" + qstr;
    const ws = this.webSocketFactory(wsUrl);
    this.ws = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "session.update", session: this._sessionConfig() }));
    };
    ws.onmessage = (e) => {
      try { this._handleEvent(JSON.parse(e.data)); } catch {}
    };
    ws.onerror = () => this._err("websocket error");
    ws.onclose = () => { if (!this.closed) this._status("closed"); };
  }

  // --------------------------------------------------------------- WebRTC path
  async _startWebrtc() {
    const pc = new RTCPeerConnection({ iceServers: [] });
    this.pc = pc;

    // outbound microphone audio
    this.stream.getAudioTracks().forEach((t) => pc.addTrack(t, this.stream));

    // remote model audio
    this.remoteAudio = document.createElement("audio");
    this.remoteAudio.autoplay = true;
    this.remoteAudio.style.display = "none";
    document.body.appendChild(this.remoteAudio);
    pc.ontrack = (e) => {
      if (e.track.kind === "audio") {
        this.remoteAudio.srcObject = e.streams[0];
        this.remoteAudio.play().catch(() => {});
      }
    };

    // control channel
    this.dc = pc.createDataChannel("oai-events");
    this.dc.onopen = () => {
      this.dc.send(JSON.stringify({ type: "session.update", session: this._sessionConfig() }));
    };
    this.dc.onmessage = (e) => { try { this._handleEvent(JSON.parse(e.data)); } catch {} };

    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState) && !this.closed) {
        this._status("closed");
      }
    };

    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    if (this.closed) return;
    await pc.setLocalDescription(offer);
    if (this.closed) return;
    await this._waitIceGathering(pc);
    if (this.closed) return;

    // Forward the chosen upstream conversation mode.
    let url = "/v1/webrtc/realtime";
    const qs = new URLSearchParams();
    if (this.realtimeMode) qs.set("realtime_mode", this.realtimeMode);
    const qstr = qs.toString();
    if (qstr) url += "?" + qstr;
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: pc.localDescription.sdp,
    });
    if (!resp.ok) throw new Error(`SDP exchange failed: ${resp.status}`);
    const answer = await resp.text();
    if (this.closed) return;
    await pc.setRemoteDescription({ type: "answer", sdp: answer });
  }

  _waitIceGathering(pc) {
    if (pc.iceGatheringState === "complete") return Promise.resolve();
    return new Promise((resolve) => {
      const check = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", check);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", check);
      // safety timeout — proceed with whatever candidates we have
      setTimeout(resolve, 1500);
    });
  }
}
